import { onUnmounted } from "vue";

/**
 * Base event map interface
 */
export interface BaseEventMap {
  [key: string]: any;
}

/**
 * Event listener function type
 */
export type EventListener<T = any> = (value: T) => void | Promise<void>;

/**
 * Base Events class
 */
class Events<TEventMap extends BaseEventMap = BaseEventMap> {
  protected events: Partial<{
    [K in keyof TEventMap]: EventListener<TEventMap[K]>[];
  }> = {};

  constructor() {
    this.events = {};
  }

  on<K extends keyof TEventMap>(
    eventname: K,
    listener: EventListener<TEventMap[K]>
  ): void {
    if (!Object.prototype.hasOwnProperty.call(this.events, eventname)) {
      this.events[eventname] = [listener];
    } else {
      this.events[eventname]!.push(listener);
    }
  }

  unshiftOn<K extends keyof TEventMap>(
    eventname: K,
    listener: EventListener<TEventMap[K]>
  ): void {
    if (!Object.prototype.hasOwnProperty.call(this.events, eventname)) {
      this.events[eventname] = [listener];
    } else {
      this.events[eventname]!.unshift(listener);
    }
  }

  /**
   * Funktion die in einer Setup-Methode einer Vue-Komponente genutzt werden kann. 
   * Entfernt die Registrierung automatisch, wenn die Komponente entfernt wird.
   */
  setupOn<K extends keyof TEventMap>(
    eventname: K,
    listener: EventListener<TEventMap[K]>
  ): void {
    this.on(eventname, listener);
    onUnmounted(() => {
      this.un(eventname, listener);
    });
  }

  un<K extends keyof TEventMap>(
    eventname: K,
    listener?: EventListener<TEventMap[K]>
  ): void {
    const event = this.events[eventname];
    if (event) {
      if (!listener) {
        event.splice(0, event.length);
      } else {
        const index = event.indexOf(listener);
        if (index !== -1) {
          event.splice(index, 1);
        }
      }
    }
  }

  hasListener<K extends keyof TEventMap>(eventname: K): boolean {
    return !!this.events[eventname]?.length;
  }

  // Alle Eventlistener triggern 
  async dispatchAll(value: any): Promise<PromiseSettledResult<void>[]> {
    const eventNames = Object.keys(this.events) as (keyof TEventMap)[];
    return Promise.allSettled(
      eventNames.map(eventname => this.dispatch(eventname, value))
    );
  }

  async dispatch<K extends keyof TEventMap>(
    eventname: K,
    value: TEventMap[K]
  ): Promise<void> {
    const listeners = this.events[eventname];
    if (listeners) {
      for (const listener of listeners) {
        await listener(value);
      }
    }
  }
}

export default Events;